import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import "./Collapsable.css";
import {getScrollElementSignature, virtualRenderingTriggerBroadcastChannel} from "../../../utilities/hooks";
import {buildClassName, getScrollElement, nameLocaleCompare} from "../../../utilities/helperFunctions";
import Text from "../Text/Text";
import InputList, {ValueList} from "../InputList/InputList";
import {useSelector} from "react-redux";
import {userSettings} from "../../../utilities/constants";
import {SettingsRowSeparator, SettingsRowValue} from "../CustomTable/CustomTable";
import {useTranslation} from "react-i18next";
import {StatusLabel} from "../Common";
import ParameterList, {ParametersTable} from "../../parameters/ParameterList";
import {getStatusIconImgContent} from "../../../utilities/iconResolver";
import TextArea from "../TextArea/TextArea";
import {OperationBacklogTable, OperationProgressTable} from "../../tables/OperationTable/OperationTable";
import {
    ErrorAlertTable,
    InfoAlertTable,
    LinksAlertTable,
    SoftErrorAlertTable,
    WarningAlertTable
} from "../Tables/Tables";
import {ChangeLogTable} from "../../tables/ChangeLogTable/ChangeLogTable";
import {InputTableV2} from "../InputTable/InputTable";
import ParameterModel from "../../../models/library/ParameterModel";

function Collapsable(props) {
    const {
        label,
        summary=label,
        children,
        containerRef,
        initialOpen=true,

        header,
        headerStyle,
        headerBar,
        summaryStyle=headerStyle,

        force,
        onToggleCb,
        className,
        flexExpand,
        isDisabled,
        ...attr
    } = props;


    const ref = useRef();
    const scrollElementRef = useRef();
    const broadcastChannel = useRef();

    const [isOpen, setIsOpen] = useState(initialOpen);
    const toggleExpanded = useCallback(event => {
        setIsOpen(event.currentTarget?.open);
    }, []);


    useEffect(() => {
        if (force != null && force.isOpen != null && isOpen !== force.isOpen) {
            setIsOpen(force.isOpen)
        }
    }, [force]);

    useEffect(() => {
        if (containerRef != null) {
            containerRef.current = ref.current;
        }
        return () => {
            if (containerRef != null) {
                containerRef.current = {};
            }
        }
    }, [containerRef, ref]);

    useEffect(() => {
        broadcastChannel.current = new BroadcastChannel(virtualRenderingTriggerBroadcastChannel);
        scrollElementRef.current = getScrollElement(ref.current);
        return () => broadcastChannel.current.close();
    }, []);

    useLayoutEffect(() => {
        if (typeof onToggleCb === 'function') {
            onToggleCb(attr.name, isOpen);
        }
        const scrollElementSignature = getScrollElementSignature(scrollElementRef.current);
        if (scrollElementSignature != null) {
            broadcastChannel.current.postMessage(scrollElementSignature);
        }
    }, [isOpen]);


    const collapsableClassName = buildClassName(
        'collapsable',
        className,
        flexExpand && 'flex-expand',
        isDisabled && 'is-disabled'
    );
    const collapsableSummaryClassName = buildClassName(
        'collapsable-summary'
    );
    const collapsableChildrenClassName = buildClassName(
        'collapsable-children',
        flexExpand && 'flex-expand'
    );


    const summaryHeader = header || summary && (
        <Text ElementTag={'span'} value={summary} isHeading/>
    );

    return (
        <details className={collapsableClassName} open={isOpen} onToggle={toggleExpanded}
            ref={ref} {...attr}
        >
            <summary className={collapsableSummaryClassName} style={headerStyle}>
                {summaryHeader}
                {headerBar}
            </summary>

            {isOpen &&
                <div className={collapsableChildrenClassName}>
                    {children}
                </div>
            }
        </details>
    )
}

export function ExpandableValueList(props) {
    const {label, values, isEditActive, listDisplay=false, ...attr} = props;

    const disabled = attr.isDisabled ? ' is-disabled' : '';
    return (
        <Collapsable label={label} isDisabled={attr.isDisabled}>
            {isEditActive ?
                <ValueList values={values} itemAriaLabel={label} {...attr}/>
                :
                listDisplay ?
                    <ul className={disabled} style={{paddingLeft: "1.5rem"}}>
                        {values.map((value, index) => <li key={index}><Text value={value} isEllipsis/></li>)}
                    </ul>
                    :
                    values.map((value, index) =>
                        <Text key={index} value={value} isEllipsis isDisabled={attr.isDisabled}/>
                    )
            }
        </Collapsable>
    );
}

export function ExpandableInputList(props) {
    const {label, values, onInputChange, isEditActive, ...attr} = props;

    return (
        <Collapsable label={label} isDisabled={attr.isDisabled}>
            {isEditActive ?
                <InputList values={values} onValueChange={onInputChange} {...attr}/>
                :
                values.map((value, index) => <label key={index} className="label is-ellipsis">{value}</label>)
            }
        </Collapsable>
    );
}

export function ExpandableTimeStampedLog(props) {
    const {label, log, isDisabled} = props;
    const initialOpen = useSelector(state => !!state.userSettingsMap.get(userSettings.TROUBLESHOOT).showObjectIds);

    return (
        <Collapsable label={label} initialOpen={initialOpen} isDisabled={isDisabled}>
            <TimeStampedLog log={log} isDisabled={isDisabled}/>
        </Collapsable>
    );
}

function TimeStampedLog(props) {
    const {log, isDisabled} = props;

    return (
        <div className="settings-table align-top">
            <div className="table-row-group">

                {log.map((row, index) => {
                    const [label, value] = row;

                    if (value == null) {
                        return (
                            <SettingsRowSeparator key={index}/>
                        )
                    }

                    return (
                        <SettingsRowValue key={index} label={label} value={value}
                            isBreakWord isNoWrap={index === 0} isDisabled={isDisabled}/>
                    )
                })}
            </div>
        </div>
    )
}

export function ExpandableParametersTable(props) {
    const {parameterLock, isDisabled, ...others} = props;
    const {t} = useTranslation(['job', 'workflow']);

    return (
        <Collapsable label={t('workflow:label.parameters')} isDisabled={isDisabled}>
            {parameterLock &&
                <StatusLabel iconName={'statusScheduled'} message={t('job:message.parameterLockPendingExecution')}
                    style={{margin: '0.125rem 0 0.5rem -0.5rem'}} isDisabled={isDisabled}/>
            }
            <ParametersTable {...others} isDisabled={isDisabled}/>
        </Collapsable>
    );
}

export function ExpandableRequiredProfilesTable(props) {
    const {requiredProfiles, executionProfileId, isDisabled} = props;
    const {t} = useTranslation(['job', 'workflow', 'fileLibrary']);

    requiredProfiles.sort(nameLocaleCompare);

    return (
        <Collapsable label={t('job:label.requiredProfiles')} isDisabled={isDisabled}>
            <div className="settings-table align-top">
                <div className="table-row-group">
                    {requiredProfiles.map((row, index) =>
                        <div className="settings-row" key={index}>
                            <div className={'settings-label-cell'}>
                                <Text value={`${row.name}`} isDisabled={isDisabled}/>
                            </div>
                            <div className="settings-value-cell">
                                {(row.missing && !!executionProfileId) &&
                                    <StatusLabel message={t('job:message.nuixProfileTypeMissing', {profileType: t(`fileLibrary:file.type.${row.profileType}`)})} isDisabled={isDisabled}/>
                                }
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Collapsable>
    );
}

export function ExpandableLog(props) {
    const {value, log=value, ...attr} = props;

    return (
        <Collapsable {...attr}>
            <Text value={log} isWordWrap
                isDisabled={attr.isDisabled}/>
        </Collapsable>
    );
}

export function ExpandableErrorLog(props) {
    const {t} = useTranslation(['common']);
    const {error, isDisabled} = props;

    return (
        <Collapsable isDisabled={isDisabled}
            header={
                <AlertHeader label={t(`common:label.errors`)} iconName={'statusError'} />
            }
        >
            <Text ElementTag={'p'} value={error}
                isWordWrap/>
        </Collapsable>
    );
}

export function ExpandableEditTextArea(props) {
    const {label, value, style, isEditActive, ...attr} = props;
    const {t} = useTranslation(['aria']);

    const disabled = attr.isDisabled ? ' is-disabled' : '';

    return (
        <Collapsable label={label} isDisabled={attr.isDisabled}>
            {isEditActive ?
                <TextArea aria-label={t('aria:input.description')} value={value} style={style} {...attr}/>
                :
                <label style={style} className={'label is-wordwrap' + disabled}>{value}</label>
            }
        </Collapsable>
    );
}

export function ExpandableOperationBacklogTable(props) {
    const {label, ...attr} = props;

    return (
        <Collapsable label={label} isDisabled={attr.isDisabled}>
            <OperationBacklogTable {...attr}/>
        </Collapsable>
    );
}

export function ExpandableOperationProgressTable(props) {
    const {label, ...attr} = props;

    return (
        <Collapsable label={label} isDisabled={attr.isDisabled}>
            <OperationProgressTable {...attr}/>
        </Collapsable>
    );
}

export function ExpandableWarningAlertTable(props) {
    const {label, ...attr} = props;

    return (
        <Collapsable isDisabled={attr.isDisabled}
            header={
                <AlertHeader label={label} iconName={'statusWarning'} />
            }
        >
            <WarningAlertTable {...attr}/>
        </Collapsable>
    );
}

export function ExpandableLinkAlertTable(props) {
    const {label, ...attr} = props;

    return (
        <Collapsable isDisabled={attr.isDisabled}
            header={
                <AlertHeader label={label} iconName={'statusLink'} />
            }
        >
            <LinksAlertTable {...attr}/>
        </Collapsable>
    );
}

export function ExpandableInfoAlertTable(props) {
    const {label, ...attr} = props;

    return (
        <Collapsable isDisabled={attr.isDisabled}
            header={
                <AlertHeader label={label} iconName={'statusInfo'} />
            }
        >
            <InfoAlertTable {...attr}/>
        </Collapsable>
    );
}

export function ExpandableSoftErrorAlertTable(props) {
    const {label, ...attr} = props;

    return (
        <Collapsable isDisabled={attr.isDisabled}
            header={
                <AlertHeader label={label} iconName={'statusSoftError'} />
            }
        >
            <SoftErrorAlertTable {...attr}/>
        </Collapsable>
    );
}


export function ExpandableErrorAlertTable(props) {
    const {label, ...attr} = props;

    return (
        <Collapsable isDisabled={attr.isDisabled}
            header={
                <AlertHeader label={label} iconName={'statusError'} />
            }
        >
            <ErrorAlertTable {...attr} />
        </Collapsable>
    );
}

export function AlertHeader(props) {
    const {
        label,
        icon,
        iconName
    } = props;

    return (
        <div className="alert-header">
            <span className="empty-span">&nbsp;</span>
            <span className="icon is-small spacing-right">
                {icon || getStatusIconImgContent(iconName)}
            </span>
            <Text ElementTag={'span'} isHeading
                value={label}/>
        </div>
    );
}

export function ExpandableChangeLogTable(props) {
    const {label, ...attr} = props;
    const initialOpen = useSelector(state => !!state.userSettingsMap.get(userSettings.TROUBLESHOOT).showObjectIds);

    return (
        <Collapsable label={label} initialOpen={initialOpen} isDisabled={attr.isDisabled}>
            <ChangeLogTable {...attr}/>
        </Collapsable>
    );
}

export function ExpandableWorkflowParametersV2(props) {
    const {label, name, force, initialOpen, isEditActive, workflowParameters, ...attr} = props;

    let body;
    if (isEditActive) {
        body = <InputTableV2 ariaLabelKey={'Parameter'} {...attr}/>
    } else {
        const parameters = new Map()
        for (const param of workflowParameters) {
            parameters.set(param[0].value, new ParameterModel({name: param[0].value, value: param[1].value}))
        }

        body = (
            <div className="display-input">
                <ParameterList parameters={parameters}/>
            </div>
        )
    }

    return (
        <Collapsable label={label} name={name} force={force} initialOpen={initialOpen}>
            {body}
        </Collapsable>
    )
}

export default Collapsable;
